<template>
  <v-container fluid>
    <v-data-table
      :headers="headers"
      :items="items.items"
      item-key="id"
      class="elevation-0 background-transparent"
      :items-per-page="25"
      :loading="loading"
      :options.sync="options"
      :server-items-length="items.meta.totalItems"
      :footer-props="{
        'items-per-page-options': rowsPerPageItemsGlobal
      }"
    >
      <template v-slot:top>
        <div class="d-inline-flex justify-space-between align-center pr-2 mb-5" style="height: 62px; width: 100%">
    
          <!-- Left-aligned elements -->
          <div class="d-inline-flex align-center">
            <v-switch 
              v-model="includeSystem" 
              data-cy="switch-include-system" 
              :label="$lang.labels.includeSystem" 
              class="pl-2 pt-1"
              inset 
              dense
            ></v-switch>
            <v-text-field
              v-model="search"
              class="ml-2 custom-search-perm"
              hide-details
              outlined
              clearable
              dense
              :label="$lang.labels.searchByName"
              data-cy="search-by-name"
            ></v-text-field>
          </div>

          <!-- Right-aligned buttons -->
          <div class="d-inline-flex align-center ml-auto">
            <v-btn
              text
              light
              color="primary"
              class="ml-2"
              @click="$emit('fetchValidations', { options: options, search: search && search.length > 1 ? search : '', resetPage: true })"
            >
              <v-icon dense small>mdi-refresh</v-icon>
            </v-btn>
            <v-btn
              v-if="roles.includes('VALIDATION_RULE_CREATOR')"
              outlined  
              color="accent"
              class="color-accent-text mr-1"
              :to="{ name: 'validationCreate', params: { lang: $lang.current_lang } }"
            >
              <v-icon
                left
                dark
              >
                mdi-file-plus
              </v-icon>
              {{ $lang.actions.create }}
            </v-btn>
          </div>

        </div>
      </template>

      <template v-slot:item.id="{ item }">
        <div class="font-weight-bold"># {{ item.id }}</div>
      </template>

      <template v-slot:item.name="{ item }">
        <div class="d-flex justify-space-between align-center">
          <!-- Name on the left with a fixed or max width -->
          <div class="text-start name-container clickable-simple">
            <router-link
              v-if="item.name.length < 86"
              class="text-decoration-none"
              :to="{ name: 'validationEdit', params: { id: item.id } }"
            >
              <span class="font-weight-regular color-primary">{{ item.name }}</span>
            </router-link>
            <v-tooltip v-else top>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <router-link
                    class="text-decoration-none"
                    :to="{ name: 'validationEdit', params: { id: item.id } }"
                  >
                    <span class="font-weight-regular color-primary">{{ item.name.substring(0, 62) }}...</span>
                  </router-link>
                </div>
              </template>
              <span>{{ item.name }}</span>
            </v-tooltip>
          </div>

          <!-- 3 dots menu on the right -->
          <!-- Disable button if no role -->
          <div>
            <v-menu
              class="custom-edit-menu"
              offset-y
              nudge-left="95"
              transition="slide-y-transition"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  variant="text"
                  icon
                  :disabled="!roles.includes('VALIDATION_RULE_CREATOR')"
                  v-on="on"
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>

              <!-- Menu with actions -->
              <!-- Hide menu content if no role -->
              <v-card
                v-if="roles.includes('VALIDATION_RULE_CREATOR')" 
                class="pr-2 pl-1"
                style="border: #CBD5E1 1px solid;"
              >
                <v-list dense>
                  <!-- Copy Create button -->
                  <v-list-item
                    link
                    class="text--secondary font-weight-light"
                    @click="copyCreateResource(item, 'validationCreate', $router, '', $options.filters.formatDateTime(new Date()), false, true)"
                  >
                    {{ $lang.actions.copyCreate }}
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </div>
        </div>
      </template>

      <template v-slot:item.createdOn="{ item }">
        <div>{{ item.createdOn | formatDateTime }}</div>
      </template>

      <template v-slot:item.modifiedOn="{ item }">
        <div>{{ item.modifiedOn | formatDateTime }}</div>
      </template>

      <template v-slot:item.isSystem="{ item }">
        <div>{{ item.isSystem ? $lang.status.YES : $lang.status.NO }}</div>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import { bus } from '@/main'
import { copyCreateResource } from '@/utils/helpers'
import { debounce } from 'lodash'
export default {
  props: {
    items: {
      type: Object,
      default: () => {}
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      lock: true,
      includeSystem: false,
      search: '',
      options: {},
      totalItems: 0,
      headers: [
        {
          text: this.$lang.labels.name,
          align: 'start',
          sortable: true,
          value: 'name'
        },
        { text: this.$lang.labels.isSystem, value: 'isSystem', sortable: true }
        // { text: this.$lang.labels.actions, value: 'actions', align: 'end', sortable: false }
      ],
      roles: []
    }
  },
  computed: {
    ...mapState('app', ['rowsPerPageItemsGlobal', 'userRoles', 'userSettings'])
  },
  watch: {
    options: {
      handler () {
        if (!this.lock) this.$emit('fetchValidations', { options: this.options, search: this.search && this.search.length > 1 ? this.search : '' })
        this.savePreFill()
      },
      deep: true
    },
    includeSystem: {
      handler (val) {
        if (!this.lock) this.$emit('includeSystem', { includeSystem: this.includeSystem, options: { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', resetPage: true } })
        this.savePreFill()
      }
    },
    search: {
      handler: debounce( function (val) {
        if (val && val.length > 1 && !this.lock) this.$emit('fetchValidations', { options: this.options, search: val, resetPage: true })
        if (!this.lock && (val === null || val === '')) this.$emit('fetchValidations', { options: this.options, search: '', resetPage: true })
        this.savePreFill()
        this.options.page = 1
      }, 500)
    }
  },
  mounted() {
    bus.$on('refreshData', (name) => {
      if (!this.lock && name === this.$route.name) this.$emit('fetchValidations', { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', resetPage: true })
      this.options.page = 1
    })
  },
  created() {
    this.roles = this.userRoles.map((x) => x.name) || []

    if (this.userSettings.display.showModifiedOn) {
      this.headers.splice(2, 0, { text: this.$lang.labels.modifiedOn, value: 'modifiedOn' })
    }

    if (this.userSettings.display.showCreatedOn) {
      this.headers.splice(2, 0, { text: this.$lang.labels.createdOn, value: 'createdOn' })
    }

    if (this.userSettings.display.showId) {
      this.headers.splice(0, 0, { text: this.$lang.header.id, value: 'id' })
    }

    if (localStorage.preFillData) {
      const preFill = JSON.parse(localStorage.preFillData)

      if (preFill && preFill.validationList) {
        this.includeSystem = preFill.validationList.includeSystem
        this.search = preFill.validationList.search
        this.options = preFill.validationList.options

        this.$emit('includeSystem', { includeSystem: this.includeSystem, options: { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', resetPage: true } })
      } else {
        this.$emit('fetchValidations', { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', resetPage: true })
      }
    } else {
      this.$emit('fetchValidations', { options: this.options, search: this.search && this.search.length > 1 ? this.search : '', resetPage: true })
    }
    setTimeout(() => this.lock = false, 100)
    this.options.page = 1
  },
  methods: {
    copyCreateResource,
    savePreFill() {
      if (!localStorage.preFillData) localStorage.preFillData = '{}'
      if (localStorage.preFillData) {
        const preFill = JSON.parse(localStorage.preFillData)

        if (!preFill.validationList) preFill.validationList = {
          includeSystem: false,
          search: '',
          options: {
            page: 1,
            itemsPerPage: 25,
            sortBy: [],
            sortDesc: []
          }
        }

        preFill.validationList.includeSystem = this.includeSystem
        preFill.validationList.search = this.search
        preFill.validationList.options = { ...this.options, page: 1 }

        localStorage.preFillData = JSON.stringify(preFill)
      }
    }
  }
}
</script>
